import React, { useState, useEffect } from 'react';
import { X, Calendar, Users, MapPin, Plane, Building2, FileText, Download, Settings } from 'lucide-react';
import api from '../../services/api';
import jsPDF from 'jspdf';

interface ItineraryViewModalProps {
  itineraryId: number;
  agencyName?: string;
  onClose: () => void;
}

interface BookingDetails {
  accommodation_cost_items: Array<{
    id: number;
    hotel_name: string;
    hotel_address?: string;
    room_type: string;
    checkin_date: string;
    checkout_date: string;
    no_of_nights: number;
    no_of_rooms: number;
    booking_status: string;
    vendor: string;
    booking_reference: string;
    remarks: string;
  }>;
  prebooked_cost_items: Array<{
    id: number;
    name: string;
    day: number;
    type: string;
    booking_status: string;
    vendor: string;
    booking_reference: string;
    remarks: string;
  }>;
  driver_paid_cost_items: Array<{
    id: number;
    name: string;
    day: number;
    type: string;
    vendor: string;
    remarks: string;
  }>;
  transportation_cost_items: Array<{
    day: number;
    trip: string;
    remarks: string;
  }>;
  transportation_handled_by: string;
}

interface SimpleBookingReportProps {
  itineraryId: number;
}

const SimpleBookingReport: React.FC<SimpleBookingReportProps> = ({ itineraryId }) => {
  const [bookingDetails, setBookingDetails] = useState<BookingDetails | null>(null);
  const [itineraryDetails, setItineraryDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [bookingResponse, itineraryResponse] = await Promise.all([
          api.get(`/booking/details/${itineraryId}`),
          api.get(`/converted-itineraries/${itineraryId}/details`)
        ]);
        
        if (bookingResponse.data.success) {
          setBookingDetails(bookingResponse.data.data);
        }
        if (itineraryResponse.data.success) {
          setItineraryDetails(itineraryResponse.data.data);
        }
      } catch (error) {
        console.error('Error fetching details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [itineraryId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'BOOKED': return 'bg-blue-100 text-blue-700';
      case 'PENDING_PAYMENT': return 'bg-yellow-100 text-yellow-700';
      case 'PAYMENT_COMPLETED': return 'bg-green-100 text-green-700';
      case 'PARTIALLY_PAID': return 'bg-orange-100 text-orange-700';
      case 'CANCELED_NO_FEE': return 'bg-red-50 text-red-600';
      case 'CANCELED_WITH_FEE': return 'bg-red-100 text-red-700';
      default: return 'bg-red-50 text-red-600'; // NOT_BOOKED highlighted in red
    }
  };

  const handleDownloadBookingHTML = () => {
    if (!bookingDetails || !itineraryDetails) return;
    
    const allActivitiesByDay: { [key: number]: Array<{ type: 'prebooked' | 'driver_paid'; item: any }> } = {};
    
    bookingDetails.prebooked_cost_items.forEach(item => {
      const day = item.day || 0;
      if (!allActivitiesByDay[day]) allActivitiesByDay[day] = [];
      allActivitiesByDay[day].push({ type: 'prebooked', item });
    });
    
    bookingDetails.driver_paid_cost_items.forEach(item => {
      const day = item.day || 0;
      if (!allActivitiesByDay[day]) allActivitiesByDay[day] = [];
      allActivitiesByDay[day].push({ type: 'driver_paid', item });
    });

    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>Travel Itinerary - ${itineraryDetails.basic_info.lead_guest}</title>
    <style>
        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            margin: 0;
            padding: 20px;
            background-color: #f8f9fa;
            color: #333;
        }
        .container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            overflow: hidden;
        }
        .header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 30px;
            text-align: center;
        }
        .header h1 {
            margin: 0 0 10px 0;
            font-size: 28px;
            font-weight: 300;
        }
        .header h2 {
            margin: 0;
            font-size: 18px;
            opacity: 0.9;
            font-weight: 400;
        }
        .content {
            padding: 30px;
        }
        .info-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin-bottom: 30px;
            padding: 20px;
            background: #f8f9fa;
            border-radius: 6px;
            border-left: 4px solid #667eea;
        }
        .info-item {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            border-bottom: 1px solid #e9ecef;
        }
        .info-item:last-child {
            border-bottom: none;
        }
        .info-label {
            font-weight: 600;
            color: #495057;
        }
        .info-value {
            color: #212529;
        }
        .day-section {
            margin-bottom: 25px;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .day-header {
            background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%);
            color: white;
            padding: 8px 20px;
            font-size: 16px;
            font-weight: 600;
        }
        .day-content {
            padding: 20px;
        }
        .activity-section {
            margin-bottom: 20px;
        }
        .activity-section:last-child {
            margin-bottom: 0;
        }
        .activity-title {
            font-size: 16px;
            font-weight: 600;
            color: #495057;
            margin-bottom: 10px;
            padding-bottom: 5px;
            border-bottom: 2px solid #e9ecef;
        }
        .activity-title.driver-paid {
            color: #fd7e14;
            border-bottom-color: #fd7e14;
        }
        .activity-title.prebooked {
            color: #28a745;
            border-bottom-color: #28a745;
        }
        .activity-item {
            padding: 8px 0;
            margin-left: 20px;
            color: #495057;
        }
        .vendor {
            font-weight: 600;
            color: #0066cc;
            margin-left: 10px;
        }
        .accommodation {
            background: #e3f2fd;
            padding: 15px;
            margin-top: 20px;
            border-radius: 6px;
            border-left: 4px solid #2196f3;
        }
        .accommodation strong {
            color: #1976d2;
            font-size: 16px;
        }
        .transportation {
            background: #f3e5f5;
            padding: 20px;
            margin-top: 30px;
            border-radius: 8px;
            border-left: 4px solid #9c27b0;
        }
        .transportation h3 {
            margin: 0 0 10px 0;
            color: #7b1fa2;
            font-size: 18px;
        }
        hr {
            border: none;
            height: 1px;
            background: linear-gradient(90deg, transparent, #dee2e6, transparent);
            margin: 20px 0;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>Your Travel Itinerary</h1>
            <h2>Trip Reference: WH_${itineraryId} | ${itineraryDetails.basic_info.lead_guest}</h2>
        </div>
        
        <div class="content">
            <div class="info-grid">
                <div>
                    <div class="info-item">
                        <span class="info-label">Lead Guest:</span>
                        <span class="info-value">${itineraryDetails.basic_info.lead_guest}</span>
                    </div>
                    <div class="info-item">
                        <span class="info-label">Number of Pax:</span>
                        <span class="info-value">${itineraryDetails.basic_info.no_of_pax}</span>
                    </div>
                    <div class="info-item">
                        <span class="info-label">Number of Kids:</span>
                        <span class="info-value">${itineraryDetails.basic_info.no_of_children}</span>
                    </div>
                    ${itineraryDetails.basic_info.no_of_children > 0 && itineraryDetails.basic_info.children_ages?.length > 0 ? `
                    <div class="info-item">
                        <span class="info-label">Kids Age:</span>
                        <span class="info-value">${itineraryDetails.basic_info.children_ages.join(', ')} years</span>
                    </div>` : ''}
                </div>
                <div>
                    <div class="info-item">
                        <span class="info-label">Travel Date:</span>
                        <span class="info-value">${new Date(itineraryDetails.basic_info.travel_date).toLocaleDateString('en-GB')}</span>
                    </div>
                    <div class="info-item">
                        <span class="info-label">No of Days:</span>
                        <span class="info-value">${itineraryDetails.basic_info.no_of_days}</span>
                    </div>
                    <div class="info-item">
                        <span class="info-label">Vehicle:</span>
                        <span class="info-value">${itineraryDetails.basic_info.vehicle_type || 'N/A'}</span>
                    </div>
                </div>
            </div>

            ${bookingDetails.transportation_handled_by ? `
            <div class="transportation">
                <h3>🚗 Transportation</h3>
                <p><strong>Your Driver:</strong> ${bookingDetails.transportation_handled_by}</p>
            </div>` : ''}

            ${itineraryDetails.accommodations && itineraryDetails.accommodations.length > 0 ? `
            <div class="accommodation-section">
                <h3 style="color: #1976d2; font-size: 18px; margin: 0 0 15px 0;">🏨 Your Accommodations</h3>
                ${[...itineraryDetails.accommodations].sort((a: any, b: any) => new Date(a.checkin_date).getTime() - new Date(b.checkin_date).getTime()).map((acc: any) => `
                <div style="background: #e3f2fd; padding: 15px; margin-bottom: 15px; border-radius: 6px; border-left: 4px solid #2196f3;">
                    <div style="font-weight: bold; color: #1976d2; font-size: 16px; margin-bottom: 10px;">${acc.hotel_name}</div>
                    ${acc.hotel_address ? `<div style="color: #495057; margin-bottom: 8px;"><strong>Address:</strong> ${acc.hotel_address}</div>` : ''}
                    <div style="color: #495057; margin-bottom: 8px;"><strong>Room Type:</strong> ${acc.room_type}</div>
                    <div style="color: #495057; margin-bottom: 8px;"><strong>Check-in:</strong> ${new Date(acc.checkin_date).toLocaleDateString('en-GB')} | <strong>Check-out:</strong> ${new Date(acc.checkout_date).toLocaleDateString('en-GB')}</div>
                    <div style="color: #495057; margin-bottom: 8px;"><strong>Rooms:</strong> ${acc.no_of_rooms} | <strong>Extra Beds:</strong> ${acc.no_of_extra_beds}</div>
                    ${acc.booking_confirmation_numbers ? `<div style="color: #495057;"><strong>Booking Reference:</strong> ${acc.booking_confirmation_numbers}</div>` : ''}
                </div>`).join('')}
            </div>` : ''}

            ${Object.entries(allActivitiesByDay)
              .sort(([a], [b]) => Number(a) - Number(b))
              .map(([day, activities]) => {
                const dayNum = Number(day);
                if (dayNum === 0) return '';
                
                const dayDetails = itineraryDetails.day_wise_details?.find((d: any) => d.day === dayNum);
                const dayDate = dayDetails ? new Date(dayDetails.tour_date).toLocaleDateString('en-GB') : '';
                
                const accommodation = itineraryDetails.accommodations?.find((acc: any) => {
                  const checkin = new Date(acc.checkin_date);
                  const checkout = new Date(acc.checkout_date);
                  const currentDay = new Date(itineraryDetails.basic_info.travel_date);
                  currentDay.setDate(currentDay.getDate() + dayNum - 1);
                  return currentDay >= checkin && currentDay < checkout;
                });
                
                const driverPaidItems = activities.filter(a => a.type === 'driver_paid');
                const prebookedItems = activities.filter(a => a.type === 'prebooked');
                
                return `
            <div class="day-section">
                <div class="day-header">
                    Day ${dayNum}${dayDate ? ` (${dayDate})` : ''}
                </div>
                <div class="day-content">
                    ${driverPaidItems.length > 0 ? `
                    <div class="activity-section">
                        <div class="activity-title driver-paid">On Spot Tickets</div>
                        ${driverPaidItems.map(({ item }) => `<div class="activity-item">&bull; ${item.name}</div>`).join('')}
                    </div>` : ''}
                    
                    ${prebookedItems.length > 0 ? `
                    <div class="activity-section">
                        <div class="activity-title prebooked">Pre-booked Activities</div>
                        ${prebookedItems.map(({ item }) => `
                        <div class="activity-item">
                            &bull; ${item.name}
                            ${item.vendor ? `<span class="vendor">@ ${item.vendor}</span>` : ''}
                        </div>`).join('')}
                    </div>` : ''}
                    
                    ${accommodation ? `
                    <div class="accommodation">
                        <strong>🏨 Tonight's Stay:</strong> ${accommodation.hotel_name}
                    </div>` : ''}
                </div>
            </div>`;
              }).join('')}
        </div>
    </div>
</body>
</html>`;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${itineraryDetails.basic_info.lead_guest}.html`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadBookingPDF = () => {
    if (!bookingDetails || !itineraryDetails) return;
    
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.width;
    const pageHeight = pdf.internal.pageSize.height;
    const margin = 20;
    const maxWidth = pageWidth - 2 * margin;
    let yPosition = 30;
    
    // Helper function to wrap text
    const wrapText = (text: string, maxWidth: number, fontSize: number = 10) => {
      pdf.setFontSize(fontSize);
      const lines = pdf.splitTextToSize(text, maxWidth);
      return Array.isArray(lines) ? lines : [lines];
    };
    
    // Helper function to check if we need a new page
    const checkPageBreak = (requiredHeight: number) => {
      if (yPosition + requiredHeight > pageHeight - 30) {
        pdf.addPage();
        yPosition = 30;
        return true;
      }
      return false;
    };
    
    // Header with gradient-like effect
    pdf.setFillColor(102, 126, 234);
    pdf.rect(0, 0, pageWidth, 35, 'F');
    
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Booking Details', pageWidth / 2, 18, { align: 'center' });
    
    pdf.setFontSize(11);
    const headerText = `WH_${itineraryId} - ${itineraryDetails.basic_info.lead_guest}`;
    const wrappedHeader = wrapText(headerText, maxWidth, 11);
    wrappedHeader.forEach((line, index) => {
      pdf.text(line, pageWidth / 2, 28 + (index * 4), { align: 'center' });
    });
    
    yPosition = 50;
    pdf.setTextColor(0, 0, 0);
    
    // Basic Info Grid
    checkPageBreak(40);
    pdf.setFillColor(248, 249, 250);
    pdf.rect(margin, yPosition - 5, maxWidth, 35, 'F');
    pdf.setDrawColor(102, 126, 234);
    pdf.setLineWidth(2);
    pdf.line(margin, yPosition - 5, margin, yPosition + 30);
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    
    const basicInfoLeft = [
      `Lead Guest: ${itineraryDetails.basic_info.lead_guest}`,
      `Number of Pax: ${itineraryDetails.basic_info.no_of_pax}`,
      `Number of Kids: ${itineraryDetails.basic_info.no_of_children}`
    ];
    
    const basicInfoRight = [
      `Travel Date: ${new Date(itineraryDetails.basic_info.travel_date).toLocaleDateString('en-GB')}`,
      `No of Days: ${itineraryDetails.basic_info.no_of_days}`,
      `Vehicle: ${itineraryDetails.basic_info.vehicle_type || 'N/A'}`
    ];
    
    if (itineraryDetails.basic_info.no_of_children > 0 && itineraryDetails.basic_info.children_ages?.length > 0) {
      basicInfoLeft.push(`Kids Age: ${itineraryDetails.basic_info.children_ages.join(', ')} years`);
    }
    
    const maxRows = Math.max(basicInfoLeft.length, basicInfoRight.length);
    for (let i = 0; i < maxRows; i++) {
      if (basicInfoLeft[i]) {
        const leftLines = wrapText(basicInfoLeft[i], (maxWidth / 2) - 10);
        leftLines.forEach((line, lineIndex) => {
          pdf.text(line, margin + 5, yPosition + (lineIndex * 4));
        });
      }
      if (basicInfoRight[i]) {
        const rightLines = wrapText(basicInfoRight[i], (maxWidth / 2) - 10);
        rightLines.forEach((line, lineIndex) => {
          pdf.text(line, pageWidth / 2 + 10, yPosition + (lineIndex * 4));
        });
      }
      yPosition += 6;
    }
    
    yPosition += 20;
    
    // Transportation section
    if (bookingDetails.transportation_handled_by) {
      checkPageBreak(25);
      
      pdf.setFillColor(243, 229, 245);
      pdf.rect(margin, yPosition - 5, maxWidth, 20, 'F');
      pdf.setDrawColor(156, 39, 176);
      pdf.setLineWidth(2);
      pdf.line(margin, yPosition - 5, margin, yPosition + 15);
      
      pdf.setTextColor(123, 31, 162);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Transportation', margin + 5, yPosition + 3);
      
      yPosition += 10;
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      const driverLines = wrapText(`Driver: ${bookingDetails.transportation_handled_by}`, maxWidth - 10);
      driverLines.forEach((line, lineIndex) => {
        pdf.text(line, margin + 5, yPosition + (lineIndex * 4));
      });
      yPosition += 20;
    }
    
    // Accommodations section
    if (itineraryDetails.accommodations && itineraryDetails.accommodations.length > 0) {
      checkPageBreak(30);
      
      pdf.setTextColor(25, 118, 210);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Accommodations', margin, yPosition);
      yPosition += 15;
      
      itineraryDetails.accommodations.forEach((acc: any) => {
        // Calculate content height first
        let contentHeight = 15; // Base height for hotel name
        
        if (acc.hotel_address) {
          const addressLines = wrapText(`Address: ${acc.hotel_address}`, maxWidth - 10);
          contentHeight += addressLines.length * 3 + 3;
        }
        contentHeight += 18; // Room type, dates, rooms info (3 lines * 6)
        if (acc.booking_confirmation_numbers) {
          const refLines = wrapText(`Booking Reference: ${acc.booking_confirmation_numbers}`, maxWidth - 10);
          contentHeight += refLines.length * 3 + 3;
        }
        contentHeight += 5; // Bottom padding
        
        checkPageBreak(contentHeight + 10);
        
        pdf.setFillColor(227, 242, 253);
        pdf.rect(margin, yPosition - 2, maxWidth, contentHeight, 'F');
        pdf.setDrawColor(33, 150, 243);
        pdf.setLineWidth(2);
        pdf.line(margin, yPosition - 2, margin, yPosition + contentHeight - 2);
        
        pdf.setTextColor(25, 118, 210);
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        const hotelLines = wrapText(acc.hotel_name, maxWidth - 10);
        hotelLines.forEach((line, lineIndex) => {
          pdf.text(line, margin + 5, yPosition + 3 + (lineIndex * 4));
        });
        yPosition += hotelLines.length * 4 + 4;
        
        pdf.setTextColor(73, 80, 87);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        
        if (acc.hotel_address) {
          const addressLines = wrapText(`Address: ${acc.hotel_address}`, maxWidth - 10);
          addressLines.forEach((line, lineIndex) => {
            pdf.text(line, margin + 5, yPosition + (lineIndex * 3));
          });
          yPosition += addressLines.length * 3 + 3;
        }
        
        pdf.text(`Room Type: ${acc.room_type}`, margin + 5, yPosition);
        yPosition += 6;
        
        pdf.text(`Check-in: ${new Date(acc.checkin_date).toLocaleDateString('en-GB')} | Check-out: ${new Date(acc.checkout_date).toLocaleDateString('en-GB')}`, margin + 5, yPosition);
        yPosition += 6;
        
        pdf.text(`Rooms: ${acc.no_of_rooms} | Extra Beds: ${acc.no_of_extra_beds}`, margin + 5, yPosition);
        yPosition += 6;
        
        if (acc.booking_confirmation_numbers) {
          const refLines = wrapText(`Booking Reference: ${acc.booking_confirmation_numbers}`, maxWidth - 10);
          refLines.forEach((line, lineIndex) => {
            pdf.text(line, margin + 5, yPosition + (lineIndex * 3));
          });
          yPosition += refLines.length * 3;
        }
        
        yPosition += 10;
      });
      
      yPosition += 10;
    }
    
    // Day-wise Activities - exactly matching HTML structure
    const allActivitiesByDay: { [key: number]: Array<{ type: 'prebooked' | 'driver_paid'; item: any }> } = {};
    
    bookingDetails.prebooked_cost_items.forEach(item => {
      const day = item.day || 0;
      if (!allActivitiesByDay[day]) allActivitiesByDay[day] = [];
      allActivitiesByDay[day].push({ type: 'prebooked', item });
    });
    
    bookingDetails.driver_paid_cost_items.forEach(item => {
      const day = item.day || 0;
      if (!allActivitiesByDay[day]) allActivitiesByDay[day] = [];
      allActivitiesByDay[day].push({ type: 'driver_paid', item });
    });
    
    // Process days in order - matching HTML
    Object.entries(allActivitiesByDay)
      .sort(([a], [b]) => Number(a) - Number(b))
      .forEach(([day, activities]) => {
        const dayNum = Number(day);
        if (dayNum === 0) return;
        
        checkPageBreak(60);
        
        const dayDetails = itineraryDetails.day_wise_details?.find((d: any) => d.day === dayNum);
        const dayDate = dayDetails ? new Date(dayDetails.tour_date).toLocaleDateString('en-GB') : '';
        
        const driverPaidItems = activities.filter(a => a.type === 'driver_paid');
        const prebookedItems = activities.filter(a => a.type === 'prebooked');
        
        // Calculate total content height
        let sectionContentHeight = 0;
        
        if (driverPaidItems.length > 0) {
          sectionContentHeight += 15; // Section title
          driverPaidItems.forEach(({ item }) => {
            const itemLines = wrapText(`• ${item.name}`, maxWidth - 20);
            sectionContentHeight += itemLines.length * 5;
          });
          sectionContentHeight += 5; // Section spacing
        }
        
        if (prebookedItems.length > 0) {
          sectionContentHeight += 15; // Section title
          prebookedItems.forEach(({ item }) => {
            const itemLines = wrapText(`• ${item.name}`, maxWidth - 20);
            sectionContentHeight += itemLines.length * 5;
            if (item.vendor) {
              const vendorLines = wrapText(`Vendor: ${item.vendor}`, maxWidth - 25);
              sectionContentHeight += vendorLines.length * 4;
            }
            sectionContentHeight += 2;
          });
          sectionContentHeight += 5; // Section spacing
        }
        
        // Check if we need accommodation space
        const accommodation = itineraryDetails.accommodations?.find((acc: any) => {
          const checkin = new Date(acc.checkin_date);
          const checkout = new Date(acc.checkout_date);
          const currentDay = new Date(itineraryDetails.basic_info.travel_date);
          currentDay.setDate(currentDay.getDate() + dayNum - 1);
          return currentDay >= checkin && currentDay < checkout;
        });
        
        if (accommodation) {
          const hotelLines = wrapText(accommodation.hotel_name, maxWidth - 60);
          sectionContentHeight += Math.max(10, hotelLines.length * 4 + 5);
        }
        
        sectionContentHeight += 10; // Bottom padding
        
        checkPageBreak(sectionContentHeight + 20);
        
        // Day section with border
        const sectionStartY = yPosition - 3;
        const sectionHeight = 10;
        
        // Draw section border with calculated height
        pdf.setDrawColor(222, 226, 230);
        pdf.setLineWidth(0.5);
        pdf.rect(margin, sectionStartY, maxWidth, sectionHeight + sectionContentHeight, 'S');
        
        // Day header with gradient
        pdf.setFillColor(79, 172, 254);
        pdf.rect(margin, sectionStartY, maxWidth, sectionHeight, 'F');
        
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        const dayHeaderText = `Day ${dayNum}${dayDate ? ` (${dayDate})` : ''}`;
        pdf.text(dayHeaderText, margin + 5, yPosition + 4);
        
        yPosition += 15;
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(10);
        
        // Driver Paid Activities section
        if (driverPaidItems.length > 0) {
          checkPageBreak(20);
          pdf.setTextColor(253, 126, 20);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Driver Paid Activities', margin + 5, yPosition);
          
          // Underline
          pdf.setDrawColor(253, 126, 20);
          pdf.setLineWidth(1);
          pdf.line(margin + 5, yPosition + 2, margin + 60, yPosition + 2);
          
          yPosition += 8;
          pdf.setTextColor(0, 0, 0);
          pdf.setFont('helvetica', 'normal');
          
          driverPaidItems.forEach(({ item }) => {
            checkPageBreak(10);
            const itemLines = wrapText(`• ${item.name}`, maxWidth - 20);
            itemLines.forEach((line, lineIndex) => {
              pdf.text(line, margin + 10, yPosition + (lineIndex * 5));
            });
            yPosition += itemLines.length * 5;
          });
          yPosition += 5;
        }
        
        // Prebooked Activities section
        if (prebookedItems.length > 0) {
          checkPageBreak(20);
          pdf.setTextColor(40, 167, 69);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Prebooked Activities', margin + 5, yPosition);
          
          // Underline
          pdf.setDrawColor(40, 167, 69);
          pdf.setLineWidth(1);
          pdf.line(margin + 5, yPosition + 2, margin + 60, yPosition + 2);
          
          yPosition += 8;
          pdf.setTextColor(0, 0, 0);
          pdf.setFont('helvetica', 'normal');
          
          prebookedItems.forEach(({ item }) => {
            checkPageBreak(15);
            const itemLines = wrapText(`• ${item.name}`, maxWidth - 20);
            itemLines.forEach((line, lineIndex) => {
              pdf.text(line, margin + 10, yPosition + (lineIndex * 5));
            });
            yPosition += itemLines.length * 5;
            
            if (item.vendor) {
              checkPageBreak(8);
              pdf.setTextColor(0, 102, 204);
              pdf.setFont('helvetica', 'bold');
              const vendorLines = wrapText(`Vendor: ${item.vendor}`, maxWidth - 25);
              vendorLines.forEach((line, lineIndex) => {
                pdf.text(line, margin + 15, yPosition + (lineIndex * 4));
              });
              yPosition += vendorLines.length * 4;
              pdf.setTextColor(0, 0, 0);
              pdf.setFont('helvetica', 'normal');
            }
            yPosition += 2;
          });
          yPosition += 5;
        }
        
        // Accommodation section
        if (accommodation) {
          checkPageBreak(15);
          pdf.setFillColor(227, 242, 253);
          pdf.rect(margin + 5, yPosition - 2, maxWidth - 10, 10, 'F');
          pdf.setDrawColor(33, 150, 243);
          pdf.setLineWidth(2);
          pdf.line(margin + 5, yPosition - 2, margin + 5, yPosition + 8);
          
          pdf.setTextColor(25, 118, 210);
          pdf.setFont('helvetica', 'bold');
          pdf.text('Accommodation:', margin + 8, yPosition + 3);
          pdf.setFont('helvetica', 'normal');
          
          const hotelLines = wrapText(accommodation.hotel_name, maxWidth - 60);
          hotelLines.forEach((line, lineIndex) => {
            pdf.text(line, margin + 50, yPosition + 3 + (lineIndex * 4));
          });
          
          pdf.setTextColor(0, 0, 0);
          yPosition += Math.max(10, hotelLines.length * 4 + 5);
        }
        
        yPosition += 10;
      });
    
    pdf.save(`${itineraryDetails.basic_info.lead_guest}_Booking_Details.pdf`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading booking details...</span>
      </div>
    );
  }

  if (!bookingDetails || !itineraryDetails) {
    return (
      <div className="text-center py-8 text-gray-500">
        <div className="text-4xl mb-2">📋</div>
        <p className="font-medium">No booking details found</p>
      </div>
    );
  }

  // Combine all activities by day
  const allActivitiesByDay: { [key: number]: Array<{ type: 'prebooked' | 'driver_paid'; item: any }> } = {};
  
  // Add prebooked activities
  bookingDetails.prebooked_cost_items.forEach(item => {
    const day = item.day || 0;
    if (!allActivitiesByDay[day]) allActivitiesByDay[day] = [];
    allActivitiesByDay[day].push({ type: 'prebooked', item });
  });
  
  // Add driver paid activities
  bookingDetails.driver_paid_cost_items.forEach(item => {
    const day = item.day || 0;
    if (!allActivitiesByDay[day]) allActivitiesByDay[day] = [];
    allActivitiesByDay[day].push({ type: 'driver_paid', item });
  });

  return (
    <div className="space-y-6">
      {/* Alert for items that need attention */}
      {(() => {
        const validStatuses = ['BOOKED', 'PENDING_PAYMENT', 'PAYMENT_COMPLETED', 'PARTIALLY_PAID'];
        const problemItems = [
          ...bookingDetails.accommodation_cost_items.filter(item => !validStatuses.includes(item.booking_status)),
          ...bookingDetails.prebooked_cost_items.filter(item => !validStatuses.includes(item.booking_status))
        ];
        
        if (problemItems.length > 0) {
          return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="text-red-600 text-xl">⚠️</span>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Items Requiring Attention</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p className="mb-2">The following items have booking issues:</p>
                    <ul className="list-disc list-inside space-y-1">
                      {problemItems.map((item, idx) => (
                        <li key={idx}>
                          <span className="font-medium">
                            {'hotel_name' in item ? item.hotel_name : item.name}
                          </span>
                          <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs">
                            {item.booking_status || 'NOT_BOOKED'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Download Buttons */}
      {bookingDetails.transportation_handled_by ? (
        <div className="flex justify-end space-x-2">
          <button
            onClick={handleDownloadBookingHTML}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            <Download className="w-4 h-4" />
            <span>Download HTML</span>
          </button>
          <button
            onClick={handleDownloadBookingPDF}
            className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
          >
            <Download className="w-4 h-4" />
            <span>Download PDF</span>
          </button>
        </div>
      ) : (
        <div className="flex justify-end">
          <div className="text-sm text-gray-500 italic">
            Download available only when driver is assigned
          </div>
        </div>
      )}

      {/* Accommodations */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-4 text-blue-800">🏨 Accommodations</h3>
        {bookingDetails.accommodation_cost_items.length === 0 ? (
          <p className="text-gray-500 text-sm">No accommodation bookings</p>
        ) : (
          <div className="space-y-3">
            {bookingDetails.accommodation_cost_items.map((item) => (
              <div key={item.id} className="border border-gray-200 rounded p-3">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-medium text-gray-900">{item.hotel_name}</h4>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.booking_status)}`}>
                    {item.booking_status || 'NOT_BOOKED'}
                  </span>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>{item.room_type} • {item.no_of_nights} nights • {item.no_of_rooms} rooms</p>
                  {(item as any).room_details?.configs && (item as any).room_details.configs.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <p className="font-medium text-gray-700">Room Configuration:</p>
                      {(item as any).room_details.configs.map((config: any, idx: number) => (
                        <p key={idx} className="text-xs pl-2">
                          • {config.room_type || item.room_type} × {config.quantity} room(s)
                          {config.family_id && ` - Family ${config.family_id}`}
                          {config.extra_beds > 0 && ` + ${config.extra_beds} extra bed(s)`}
                          {config.kids_bf > 0 && ` + ${config.kids_bf} kids breakfast`}
                        </p>
                      ))}
                    </div>
                  )}
                  <p>Check-in: {new Date(item.checkin_date).toLocaleDateString('en-GB')} | Check-out: {new Date(item.checkout_date).toLocaleDateString('en-GB')}</p>
                  {item.hotel_address && <p>Address: {item.hotel_address}</p>}
                  {item.vendor && (
                    <p>
                      <span className="font-medium text-blue-700">Vendor: {item.vendor}</span>
                    </p>
                  )}
                  {item.booking_reference && <p>Reference: {item.booking_reference}</p>}
                  {item.remarks && <p>Remarks: {item.remarks}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Day-wise Activities */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">📅 Trip Details by Day</h3>
        {Object.keys(allActivitiesByDay).length === 0 ? (
          <p className="text-gray-500 text-sm">No activities found</p>
        ) : (
          <div className="space-y-4">
            {Object.entries(allActivitiesByDay)
              .sort(([a], [b]) => Number(a) - Number(b))
              .map(([day, activities]) => {
                const dayNum = Number(day);
                if (dayNum === 0) return null; // Skip general items for day view
                
                const dayDetails = itineraryDetails.day_wise_details?.find((d: any) => d.day === dayNum);
                const dayDate = dayDetails ? new Date(dayDetails.tour_date).toLocaleDateString('en-GB') : '';
                
                // Find accommodation for this day
                const accommodation = itineraryDetails.accommodations?.find((acc: any) => {
                  const checkin = new Date(acc.checkin_date);
                  const checkout = new Date(acc.checkout_date);
                  const currentDay = new Date(itineraryDetails.basic_info.travel_date);
                  currentDay.setDate(currentDay.getDate() + dayNum - 1);
                  return currentDay >= checkin && currentDay < checkout;
                });
                
                const driverPaidItems = activities.filter(a => a.type === 'driver_paid');
                const prebookedItems = activities.filter(a => a.type === 'prebooked');
                
                return (
                  <div key={day} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">
                      Day {dayNum}{dayDate ? ` (${dayDate})` : ''}
                    </h4>
                    
                    {/* Driver Paid Activities */}
                    {driverPaidItems.length > 0 && (
                      <div className="mb-3">
                        <h5 className="text-sm font-medium text-orange-700 mb-2">Driver Paid:</h5>
                        <div className="space-y-1 ml-4">
                          {driverPaidItems.map(({ item }, idx) => (
                            <div key={idx} className="text-sm text-gray-700">
                              • {item.name}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Prebooked Activities */}
                    {prebookedItems.length > 0 && (
                      <div className="mb-3">
                        <h5 className="text-sm font-medium text-green-700 mb-2">Prebooked:</h5>
                        <div className="space-y-2 ml-4">
                          {prebookedItems.map(({ item }, idx) => (
                            <div key={idx} className="flex items-center justify-between">
                              <div className="text-sm text-gray-700">
                                • {item.name}
                                {item.vendor && (
                                  <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-sm font-medium">
                                    {item.vendor}
                                  </span>
                                )}
                              </div>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(item.booking_status)}`}>
                                {item.booking_status || 'NOT_BOOKED'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Accommodation for this day */}
                    {accommodation && (
                      <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
                        <span className="text-sm font-medium text-blue-800">Accommodation: {accommodation.hotel_name}</span>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
};

interface OperationalRemarksProps {
  itineraryId: number;
}

const OperationalRemarks: React.FC<OperationalRemarksProps> = ({ itineraryId }) => {
  const [remarks, setRemarks] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRemarks = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/booking/details/${itineraryId}`);
        if (response.data.success && response.data.data.operation_team_remarks) {
          setRemarks(response.data.data.operation_team_remarks);
        }
      } catch (error) {
        console.error('Error fetching operational remarks:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRemarks();
  }, [itineraryId]);

  if (loading) {
    return (
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div className="animate-pulse">
          <div className="h-4 bg-orange-200 rounded w-1/4 mb-2"></div>
          <div className="h-3 bg-orange-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  if (!remarks) {
    return null;
  }

  return (
    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
      <div className="flex items-start gap-2">
        <span className="text-orange-600 font-semibold text-sm flex-shrink-0">📝 Operation Team Remarks:</span>
        <p className="text-orange-800 text-sm font-medium">{remarks}</p>
      </div>
    </div>
  );
};

interface ItineraryViewModalProps {
  itineraryId: number;
  agencyName?: string;
  onClose: () => void;
}

interface ItineraryDetails {
  basic_info: {
    itinerary_name: string;
    lead_guest: string;
    no_of_pax: number;
    no_of_children: number;
    no_of_days: number;
    travel_date: string;
    created_date: string;
    vehicle_type: string;
    children_ages: number[];
    paging?: string;
  };
  accommodations: Array<{
    id: number | null;
    hotel_name: string;
    hotel_address: string;
    address_source?: string;
    room_type: string;
    checkin_date: string;
    checkout_date: string;
    no_of_rooms: number;
    no_of_extra_beds: number;
    booking_confirmation_numbers: string;
    room_details?: any;
  }>;
  flights: Array<{
    id: number | null;
    flight_type: string;
    flight_no: string;
    from_airport: string;
    to_airport: string;
    takeoff_time: string;
    landing_time: string;
  }>;
  day_wise_details: Array<{
    day: number;
    tour_title: string;
    tour_description: string;
    tour_date: string;
    meals: {
      lunch?: string;
      dinner?: string;
    };
    day_addons: Array<{
      activity_name: string;
      display_name: string;
      calculated_adults: number;
      calculated_children: number;
      display_in_itinerary?: boolean;
    }>;
  }>;
  additional_activities: Array<{
    activity_name: string;
    calculated_adults: number;
    calculated_children: number;
    activity_cost: number;
  }>;
}

const ItineraryViewModal: React.FC<ItineraryViewModalProps> = ({ itineraryId, agencyName, onClose }) => {
  const [itinerary, setItinerary] = useState<ItineraryDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('basic');
  const [guestName, setGuestName] = useState('');
  const [paging, setPaging] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accommodations, setAccommodations] = useState<ItineraryDetails['accommodations']>([]);
  const [flights, setFlights] = useState<ItineraryDetails['flights']>([]);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchItineraryDetails();
    // Add scroll lock
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.overscrollBehavior = 'auto';
    };
  }, [itineraryId]);

  const fetchItineraryDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/converted-itineraries/${itineraryId}/details`);
      if (response.data.success) {
        setItinerary(response.data.data);
        setGuestName(response.data.data.basic_info.lead_guest);
        setPaging(response.data.data.basic_info.paging || '');
        setAccommodations(response.data.data.accommodations);
        setFlights(response.data.data.flights);
      }
    } catch (error) {
      console.error('Error fetching itinerary details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      
      await api.put(`/converted-itineraries/${itineraryId}/guest`, {
        lead_guest: guestName
      });
      
      await api.put(`/converted-itineraries/${itineraryId}/paging`, {
        paging: paging
      });
      
      // Save accommodations
      await api.put(`/converted-itineraries/${itineraryId}/accommodations`, {
        accommodations: accommodations
      });
      
      // Save flights - no datetime formatting needed as backend expects YYYY-MM-DDTHH:MM format
      await api.put(`/converted-itineraries/${itineraryId}/flights`, {
        flights: flights
      });
      
      if (itinerary) {
        setItinerary({
          ...itinerary,
          basic_info: {
            ...itinerary.basic_info,
            lead_guest: guestName,
            paging: paging
          },
          accommodations: accommodations,
          flights: flights
        });
      }
      
      setEditMode(false);
    } catch (error) {
      console.error('Error updating itinerary:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelAll = () => {
    setGuestName(itinerary?.basic_info.lead_guest || '');
    setPaging(itinerary?.basic_info.paging || '');
    setAccommodations(itinerary?.accommodations || []);
    setFlights(itinerary?.flights || []);
    setEditMode(false);
  };

  const handleDownload = async (type: 'confirmation' | 'paging' | 'agent') => {
    try {
      const response = await api.get(`/converted-itineraries/${itineraryId}/download/${type}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Match Streamlit filename conventions
      if (type === 'confirmation') {
        link.download = `Booking Confirmation_WH_${itineraryId}.docx`;
      } else if (type === 'agent') {
        link.download = `Booking Confirmation_WH_${itineraryId}.docx`;
      } else if (type === 'paging') {
        link.download = `Paging_WH_${itineraryId}.docx`;
      }
      
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error(`Error downloading ${type}:`, error);
      let errorMessage = 'An error occurred while downloading the document. Please contact your administrator.';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.status === 400 && error.response?.data) {
        // Handle blob error response
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
        }
      }
      
      setError(errorMessage);
      setTimeout(() => setError(null), 5000);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading itinerary details...</p>
        </div>
      </div>
    );
  }

  if (!itinerary) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <p className="text-red-600">Failed to load itinerary details</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-500 text-white rounded">
            Close
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: FileText },
    { id: 'accommodations', label: 'Hotels', icon: Building2 },
    { id: 'flights', label: 'Flights', icon: Plane },
    { id: 'itinerary', label: 'Day-wise', icon: Calendar },
    { id: 'booking', label: 'Booking', icon: Settings },
    { id: 'customer', label: 'Customer Itinerary', icon: FileText }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl w-full max-w-[95vw] h-[95vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-gray-50 rounded-t-xl flex-shrink-0">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
            WH_{itineraryId} - {itinerary.basic_info.lead_guest}
            {agencyName && <span className="text-gray-600"> | {agencyName}</span>}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-200 transition-colors">
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 bg-white flex-shrink-0">
          <nav className="flex overflow-x-auto scrollbar-hide px-2 sm:px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-3 sm:py-4 px-3 sm:px-4 border-b-2 font-medium text-xs sm:text-sm flex items-center space-x-1 sm:space-x-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 min-h-0">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <X className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
              <button
                onClick={() => setError(null)}
                className="flex-shrink-0 ml-4 text-red-400 hover:text-red-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          {activeTab === 'basic' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lead Guest</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={guestName}
                      onChange={(e) => setGuestName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Enter guest name"
                    />
                  ) : (
                    <p className="text-sm text-gray-900">{itinerary.basic_info.lead_guest}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">No of Pax</label>
                  <p className="text-sm text-gray-900">{itinerary.basic_info.no_of_pax}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">No of Children</label>
                  <p className="text-sm text-gray-900">{itinerary.basic_info.no_of_children}</p>
                </div>
                {itinerary.basic_info.children_ages && itinerary.basic_info.children_ages.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Children Ages</label>
                    <p className="text-sm text-gray-900">{itinerary.basic_info.children_ages.join(', ')} years</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">No of Days</label>
                  <p className="text-sm text-gray-900">{itinerary.basic_info.no_of_days}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Travel Date</label>
                  <p className="text-sm text-gray-900">
                    {new Date(itinerary.basic_info.travel_date).toLocaleDateString('en-GB')}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Name</label>
                  <p className="text-sm text-gray-900">{itinerary.basic_info.vehicle_type}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Paging</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={paging}
                      onChange={(e) => setPaging(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Enter paging information"
                    />
                  ) : (
                    <p className="text-sm text-gray-900">{itinerary.basic_info.paging || 'Not set'}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'accommodations' && (
            <div className="space-y-4">
              {editMode && (
                <div className="flex justify-end gap-2 mb-4">
                  <button
                    onClick={async () => {
                      if (!window.confirm('This will delete all current accommodations and recreate them from the itinerary data. Continue?')) return;
                      try {
                        setSyncing(true);
                        const response = await api.post(`/converted-itineraries/${itineraryId}/resync-accommodations`);
                        if (response.status === 200) {
                          alert(`Successfully resynced ${response.data.created_count} accommodations from itinerary data`);
                          fetchItineraryDetails();
                        }
                      } catch (error) {
                        console.error('Error resyncing accommodations:', error);
                        alert('Error resyncing accommodations. Please try again.');
                      } finally {
                        setSyncing(false);
                      }
                    }}
                    disabled={syncing}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {syncing ? 'Resyncing...' : 'Resync from Itinerary'}
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        setSyncing(true);
                        const response = await api.post(`/converted-itineraries/${itineraryId}/sync-hotel-addresses`);
                        if (response.status === 200) {
                          alert(`Successfully synced ${response.data.updated_count} hotel addresses from database`);
                          fetchItineraryDetails();
                        }
                      } catch (error) {
                        console.error('Error syncing hotel addresses:', error);
                        alert('Error syncing hotel addresses. Please try again.');
                      } finally {
                        setSyncing(false);
                      }
                    }}
                    disabled={syncing}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {syncing ? 'Syncing...' : 'Sync Hotel Addresses'}
                  </button>
                </div>
              )}
              {editMode ? (
                <div className="space-y-4">
                  {accommodations.map((acc, index) => (
                    <div key={acc.id || index} className="border border-gray-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Hotel Name</label>
                          <input
                            type="text"
                            value={acc.hotel_name}
                            onChange={(e) => {
                              const updated = [...accommodations];
                              updated[index].hotel_name = e.target.value;
                              setAccommodations(updated);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Hotel Address</label>
                          <input
                            type="text"
                            value={acc.hotel_address}
                            onChange={(e) => {
                              const updated = [...accommodations];
                              updated[index].hotel_address = e.target.value;
                              setAccommodations(updated);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            placeholder="Enter hotel address"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Room Type</label>
                          <input
                            type="text"
                            value={acc.room_type}
                            onChange={(e) => {
                              const updated = [...accommodations];
                              updated[index].room_type = e.target.value;
                              setAccommodations(updated);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Check-in Date</label>
                          <input
                            type="date"
                            value={acc.checkin_date}
                            onChange={(e) => {
                              const updated = [...accommodations];
                              updated[index].checkin_date = e.target.value;
                              setAccommodations(updated);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Check-out Date</label>
                          <input
                            type="date"
                            value={acc.checkout_date}
                            onChange={(e) => {
                              const updated = [...accommodations];
                              updated[index].checkout_date = e.target.value;
                              setAccommodations(updated);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">No of Rooms</label>
                          <input
                            type="number"
                            value={acc.no_of_rooms}
                            onChange={(e) => {
                              const updated = [...accommodations];
                              updated[index].no_of_rooms = parseInt(e.target.value) || 0;
                              setAccommodations(updated);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Confirmation Numbers</label>
                          <input
                            type="text"
                            value={acc.booking_confirmation_numbers}
                            onChange={(e) => {
                              const updated = [...accommodations];
                              updated[index].booking_confirmation_numbers = e.target.value;
                              setAccommodations(updated);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                      </div>
                      {(acc as any).room_details?.configs && (acc as any).room_details.configs.length > 0 && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                          <div className="text-sm font-medium text-blue-900 mb-2">Room Configuration (Read-only):</div>
                          <div className="space-y-1">
                            {(acc as any).room_details.configs.map((config: any, idx: number) => (
                              <div key={idx} className="text-sm text-blue-800">
                                • {config.room_type} × {config.quantity}
                                {config.extra_beds > 0 && ` + ${config.extra_beds} extra bed(s)`}
                                {config.kids_bf > 0 && ` + ${config.kids_bf} kids breakfast`}
                              </div>
                            ))}
                          </div>
                          <div className="text-xs text-blue-700 mt-2 italic">
                            Note: Room configurations are set during itinerary creation and cannot be edited here.
                          </div>
                        </div>
                      )}
                      {accommodations.length > 1 && (
                        <button
                          onClick={() => {
                            const updated = accommodations.filter((_, i) => i !== index);
                            setAccommodations(updated);
                          }}
                          className="mt-2 px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      setAccommodations([...accommodations, {
                        id: null,
                        hotel_name: '',
                        hotel_address: '',
                        address_source: 'MANUAL',
                        room_type: '',
                        checkin_date: '',
                        checkout_date: '',
                        no_of_rooms: 1,
                        no_of_extra_beds: 0,
                        booking_confirmation_numbers: '',
                        room_details: null
                      }]);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                  >
                    Add Hotel
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <div className="inline-block min-w-full align-middle">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hotel</th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Room Type</th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check-in</th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Check-out</th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rooms</th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Confirmation</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {itinerary.accommodations.map((acc) => (
                          <tr key={acc.id}>
                            <td className="px-3 sm:px-6 py-4">
                              <div>
                                <div className="text-xs sm:text-sm font-medium text-gray-900">{acc.hotel_name}</div>
                                <div className="text-xs text-gray-500 flex items-center gap-1">
                                  <span>{acc.hotel_address}</span>
                                  {acc.address_source === 'HOTEL_DB' && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" title="Address from hotel database">
                                      DB
                                    </span>
                                  )}
                                </div>
                                {(acc as any).room_details?.configs && (acc as any).room_details.configs.length > 0 && (
                                  <div className="mt-2 space-y-1">
                                    <div className="text-xs font-medium text-gray-700">Room Configuration:</div>
                                    {(acc as any).room_details.configs.map((config: any, idx: number) => (
                                      <div key={idx} className="text-xs text-gray-600 pl-2">
                                        • {config.room_type} × {config.quantity}
                                        {config.extra_beds > 0 && ` + ${config.extra_beds} extra bed(s)`}
                                        {config.kids_bf > 0 && ` + ${config.kids_bf} kids breakfast`}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm text-gray-900">
                              {(acc as any).room_details?.configs && (acc as any).room_details.configs.length > 0 ? (
                                <span className="text-gray-500 italic">See config</span>
                              ) : (
                                acc.room_type
                              )}
                            </td>
                            <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm text-gray-900">
                              {new Date(acc.checkin_date).toLocaleDateString('en-GB')}
                            </td>
                            <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm text-gray-900">
                              {new Date(acc.checkout_date).toLocaleDateString('en-GB')}
                            </td>
                            <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm text-gray-900">
                              {(acc as any).room_details?.configs && (acc as any).room_details.configs.length > 0 ? (
                                <span className="text-gray-500 italic">See config</span>
                              ) : (
                                `${acc.no_of_rooms} rooms, ${acc.no_of_extra_beds} extra beds`
                              )}
                            </td>
                            <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm text-gray-900">
                              {acc.booking_confirmation_numbers}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'flights' && (
            <div className="space-y-4">
              {editMode ? (
                <div className="space-y-4">
                  {flights.map((flight, index) => (
                    <div key={flight.id || index} className="border border-gray-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Flight Type</label>
                          <select
                            value={flight.flight_type}
                            onChange={(e) => {
                              const updated = [...flights];
                              updated[index].flight_type = e.target.value;
                              setFlights(updated);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          >
                            <option value="Arrival">Arrival</option>
                            <option value="Departure">Departure</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Flight Number</label>
                          <input
                            type="text"
                            value={flight.flight_no}
                            onChange={(e) => {
                              const updated = [...flights];
                              updated[index].flight_no = e.target.value;
                              setFlights(updated);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">From Airport</label>
                          <input
                            type="text"
                            value={flight.from_airport}
                            onChange={(e) => {
                              const updated = [...flights];
                              updated[index].from_airport = e.target.value;
                              setFlights(updated);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">To Airport</label>
                          <input
                            type="text"
                            value={flight.to_airport}
                            onChange={(e) => {
                              const updated = [...flights];
                              updated[index].to_airport = e.target.value;
                              setFlights(updated);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Takeoff Time</label>
                          <input
                            type="datetime-local"
                            value={flight.takeoff_time ? flight.takeoff_time.slice(0, 16) : ''}
                            onChange={(e) => {
                              const updated = [...flights];
                              updated[index].takeoff_time = e.target.value;
                              setFlights(updated);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Landing Time</label>
                          <input
                            type="datetime-local"
                            value={flight.landing_time ? flight.landing_time.slice(0, 16) : ''}
                            onChange={(e) => {
                              const updated = [...flights];
                              updated[index].landing_time = e.target.value;
                              setFlights(updated);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const updated = flights.filter((_, i) => i !== index);
                          setFlights(updated);
                        }}
                        className="mt-2 px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      setFlights([...flights, {
                        id: null,
                        flight_type: 'Arrival',
                        flight_no: '',
                        from_airport: '',
                        to_airport: '',
                        takeoff_time: '',
                        landing_time: ''
                      }]);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                  >
                    Add Flight
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <div className="inline-block min-w-full align-middle">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Flight No</th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">From</th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">To</th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Departure</th>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Arrival</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {itinerary.flights.map((flight) => (
                          <tr key={flight.id}>
                            <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm text-gray-900">{flight.flight_type}</td>
                            <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm text-gray-900">{flight.flight_no}</td>
                            <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm text-gray-900">{flight.from_airport}</td>
                            <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm text-gray-900">{flight.to_airport}</td>
                            <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm text-gray-900">
                              {new Date(flight.takeoff_time).toLocaleDateString('en-GB')}, {new Date(flight.takeoff_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm text-gray-900">
                              {new Date(flight.landing_time).toLocaleDateString('en-GB')}, {new Date(flight.landing_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'itinerary' && (
            <div className="space-y-6">
              {itinerary.day_wise_details.map((day) => (
                <div key={day.day} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-2">
                    Day {day.day} ({new Date(day.tour_date).toLocaleDateString('en-GB')}): {day.tour_title}
                  </h3>
                  <div className="text-sm text-gray-700 mb-3">
                    {day.tour_description.split('\n').map((line, idx) => (
                      <p key={idx} className="mb-1">• {line.replace(/^- /, '')}</p>
                    ))}
                  </div>
                  
                  {/* Day Addons */}
                  {day.day_addons && day.day_addons.filter(addon => (addon as any).display_in_itinerary !== false).length > 0 && (
                    <div className="mb-3">
                      <h4 className="text-sm font-semibold text-gray-800 mb-2">Day Addons:</h4>
                      <div className="space-y-1">
                        {day.day_addons.filter(addon => (addon as any).display_in_itinerary !== false).map((addon, idx) => (
                          <div key={idx} className="text-sm text-gray-600 ml-2">
                            • {addon.calculated_adults > 0 && `${addon.calculated_adults} x `}{addon.activity_name}
                            {addon.calculated_children > 0 && ` (${addon.calculated_children} children)`}
                          </div>
                        ))}
                        {(day as any).day_extras && (day as any).day_extras.filter((extra: any) => extra.display_in_itinerary !== false && extra.number > 0 && extra.extra_text).map((extra: any, idx: number) => (
                          <div key={`extra-${idx}`} className="text-sm text-gray-600 ml-2">
                            • {extra.number} x {extra.extra_text}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Show day_extras even if no day_addons */}
                  {(!day.day_addons || day.day_addons.filter(addon => (addon as any).display_in_itinerary !== false).length === 0) && (day as any).day_extras && (day as any).day_extras.filter((extra: any) => extra.display_in_itinerary !== false && extra.number > 0 && extra.extra_text).length > 0 && (
                    <div className="mb-3">
                      <h4 className="text-sm font-semibold text-gray-800 mb-2">Day Addons:</h4>
                      <div className="space-y-1">
                        {(day as any).day_extras.filter((extra: any) => extra.display_in_itinerary !== false && extra.number > 0 && extra.extra_text).map((extra: any, idx: number) => (
                          <div key={idx} className="text-sm text-gray-600 ml-2">
                            • {extra.number} x {extra.extra_text}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {(day.meals.lunch || day.meals.dinner) && (
                    <div className="text-sm">
                      <strong>Meals:</strong>
                      {day.meals.lunch && (
                        <div className="ml-2">
                          <span className="font-medium">Lunch:</span> {day.meals.lunch}
                        </div>
                      )}
                      {day.meals.dinner && (
                        <div className="ml-2">
                          <span className="font-medium">Dinner:</span> {day.meals.dinner}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              
              {/* Additional Activities */}
              {itinerary.additional_activities && itinerary.additional_activities.length > 0 && (
                <div className="border border-gray-200 rounded-lg p-4 bg-blue-50">
                  <h3 className="text-lg font-semibold mb-3 text-blue-800">Additional Activities</h3>
                  <div className="space-y-2">
                    {itinerary.additional_activities.map((activity, idx) => (
                      <div key={idx} className="text-sm text-gray-700">
                        • {activity.calculated_adults > 0 && `${activity.calculated_adults} x `}{activity.activity_name}
                        {activity.calculated_children > 0 && ` (${activity.calculated_children} children)`}
                        {activity.activity_cost > 0 && (
                          <span className="text-green-600 font-medium ml-2">
                            Cost: {activity.activity_cost.toLocaleString()}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'booking' && (
            <SimpleBookingReport itineraryId={itineraryId} />
          )}

          {activeTab === 'customer' && (
            <div className="space-y-6 max-w-4xl mx-auto">
              {/* Basic Information */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h2 className="text-xl font-bold text-center mb-6 text-gray-900">BOOKING CONFIRMATION</h2>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="font-semibold">Trip ID:</span>
                      <span>WH_{itineraryId}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Lead Guest:</span>
                      <span>{itinerary.basic_info.lead_guest}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">No of Pax:</span>
                      <span>{itinerary.basic_info.no_of_pax}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">No of Days:</span>
                      <span>{itinerary.basic_info.no_of_days}</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="font-semibold">No of Children:</span>
                      <span>{itinerary.basic_info.no_of_children}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Children Ages:</span>
                      <span>{itinerary.basic_info.children_ages?.join(', ') || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Vehicle:</span>
                      <span>{itinerary.basic_info.vehicle_type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-semibold">Travel Date:</span>
                      <span>{new Date(itinerary.basic_info.travel_date).toLocaleDateString('en-GB')}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Accommodations */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">ACCOMMODATIONS</h3>
                <div className="space-y-4">
                  {itinerary.accommodations.map((acc, index) => (
                    <div key={index} className="border border-gray-300 rounded p-4">
                      <div className="mb-2">
                        <span className="font-semibold">Hotel: </span>{acc.hotel_name}
                      </div>
                      <div className="mb-2">
                        <span className="font-semibold">Room type: </span>{acc.room_type}
                      </div>
                      <div className="mb-3">
                        <span className="font-semibold">Hotel Address: </span>{acc.hotel_address}
                      </div>
                      
                      <div className="grid grid-cols-5 gap-2 text-sm">
                        <div>
                          <div className="font-semibold mb-1">Check-in</div>
                          <div>{new Date(acc.checkin_date).toLocaleDateString('en-GB')}</div>
                        </div>
                        <div>
                          <div className="font-semibold mb-1">Check-out</div>
                          <div>{new Date(acc.checkout_date).toLocaleDateString('en-GB')}</div>
                        </div>
                        <div>
                          <div className="font-semibold mb-1">No. of Rooms</div>
                          <div>{acc.no_of_rooms}</div>
                        </div>
                        <div>
                          <div className="font-semibold mb-1">No. of Extra Beds</div>
                          <div>{acc.no_of_extra_beds}</div>
                        </div>
                        <div>
                          <div className="font-semibold mb-1">Booking Confirmation</div>
                          <div>{acc.booking_confirmation_numbers || 'N/A'}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Flights */}
              {itinerary.flights && itinerary.flights.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">FLIGHTS</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="border border-gray-300 px-3 py-2 text-left font-semibold">Type</th>
                          <th className="border border-gray-300 px-3 py-2 text-left font-semibold">Flight No</th>
                          <th className="border border-gray-300 px-3 py-2 text-left font-semibold">From Airport</th>
                          <th className="border border-gray-300 px-3 py-2 text-left font-semibold">Takeoff Time</th>
                          <th className="border border-gray-300 px-3 py-2 text-left font-semibold">To Airport</th>
                          <th className="border border-gray-300 px-3 py-2 text-left font-semibold">Landing Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {itinerary.flights.map((flight, index) => (
                          <tr key={index}>
                            <td className="border border-gray-300 px-3 py-2">{flight.flight_type}</td>
                            <td className="border border-gray-300 px-3 py-2">{flight.flight_no}</td>
                            <td className="border border-gray-300 px-3 py-2">{flight.from_airport}</td>
                            <td className="border border-gray-300 px-3 py-2">
                              {flight.takeoff_time ? new Date(flight.takeoff_time).toLocaleString('en-GB') : 'N/A'}
                            </td>
                            <td className="border border-gray-300 px-3 py-2">{flight.to_airport}</td>
                            <td className="border border-gray-300 px-3 py-2">
                              {flight.landing_time ? new Date(flight.landing_time).toLocaleString('en-GB') : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Day Wise Details */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">DAY WISE DETAILS</h3>
                <div className="space-y-6">
                  {itinerary.day_wise_details.map((day) => (
                    <div key={day.day} className="border-l-4 border-blue-500 pl-4">
                      <h4 className="font-semibold text-gray-900 mb-2">
                        DAY {day.day.toString().padStart(2, '0')} ({new Date(day.tour_date).toLocaleDateString('en-GB')}): {day.tour_title.toUpperCase()}
                      </h4>
                      
                      <div className="text-sm text-gray-700 mb-3 space-y-1">
                        {day.tour_description.split('\n').map((line, idx) => {
                          const cleanLine = line.replace(/^- /, '').trim();
                          return cleanLine ? (
                            <div key={idx} className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>{cleanLine}</span>
                            </div>
                          ) : null;
                        })}
                      </div>
                      
                      {/* Day Addons - Same format as itinerary print */}
                      {((day.day_addons && day.day_addons.filter(addon => (addon as any).display_in_itinerary !== false).length > 0) || ((day as any).day_extras && (day as any).day_extras.filter((extra: any) => extra.display_in_itinerary !== false && extra.number > 0 && extra.extra_text).length > 0)) && (
                        <div className="mb-3">
                          <span className="text-sm italic text-gray-600">
                            Addons Included: {[
                              ...(day.day_addons ? day.day_addons.filter(addon => (addon as any).display_in_itinerary !== false).map(addon => {
                                return `${addon.calculated_adults} x ${addon.display_name || addon.activity_name}`;
                              }) : []),
                              ...((day as any).day_extras ? (day as any).day_extras.filter((extra: any) => extra.display_in_itinerary !== false && extra.number > 0 && extra.extra_text).map((extra: any) => 
                                `${extra.number} x ${extra.extra_text}`
                              ) : [])
                            ].join(', ')}
                          </span>
                        </div>
                      )}
                      
                      {/* Meals */}
                      {(day.meals.lunch || day.meals.dinner) && (
                        <div className="text-sm">
                          <div className="font-semibold underline mb-1">Meals</div>
                          {day.meals.lunch && (
                            <div className="flex items-start ml-4">
                              <span className="mr-2">•</span>
                              <span><span className="font-medium">Lunch:</span> {day.meals.lunch}</span>
                            </div>
                          )}
                          {day.meals.dinner && (
                            <div className="flex items-start ml-4">
                              <span className="mr-2">•</span>
                              <span><span className="font-medium">Dinner:</span> {day.meals.dinner}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Activities - Same format as itinerary print */}
              {itinerary.additional_activities && itinerary.additional_activities.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">Addons Included</h3>
                  <div className="space-y-2">
                    {itinerary.additional_activities.map((activity, idx) => (
                      <div key={idx} className="text-sm text-gray-700 border border-gray-300 p-3 rounded">
                        {activity.calculated_adults + activity.calculated_children} x {activity.activity_name}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Operational Remarks */}
              <OperationalRemarks itineraryId={itineraryId} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between p-4 sm:p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl flex-shrink-0 gap-3 sm:gap-0">
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <button
              onClick={() => handleDownload('confirmation')}
              className="flex items-center justify-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-xs flex-1 sm:flex-none"
            >
              <Download className="w-3 h-3" />
              <span>Confirmation</span>
            </button>
            <button
              onClick={() => handleDownload('paging')}
              className="flex items-center justify-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-xs flex-1 sm:flex-none"
            >
              <Download className="w-3 h-3" />
              <span>Paging</span>
            </button>
            <button
              onClick={() => handleDownload('agent')}
              className="flex items-center justify-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-xs flex-1 sm:flex-none"
            >
              <Download className="w-3 h-3" />
              <span>Agent Copy</span>
            </button>
            {editMode ? (
              <button
                onClick={handleSaveAll}
                disabled={saving}
                className="flex items-center justify-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm flex-1 sm:flex-none disabled:opacity-50"
              >
                <span>{saving ? 'Updating...' : 'Update'}</span>
              </button>
            ) : (
              <button
                onClick={() => setEditMode(true)}
                className="flex items-center justify-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm flex-1 sm:flex-none"
              >
                <span>Edit</span>
              </button>
            )}
          </div>
          <div className="flex gap-2">
            {editMode && (
              <button
                onClick={handleCancelAll}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
              >
                Cancel
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItineraryViewModal;